import type { Appearance } from '@stripe/stripe-js';

import { getRGB } from './color';

export const appearanceColors = [
  'primary',
  'secondary',
  'gray-100',
  'gray-200',
  'gray-500',
  'gray-700',
  'link',
  'danger',
  'success',
  'white',
  'black',
  'text',
  'disabled',
  'darkDisabled',
  'header-background',
  'header-text',
  'background',
] as const;

export type IAppearanceColor = (typeof appearanceColors)[number];
type IAppearanceHexColors = Record<IAppearanceColor, string>;
type IAppearanceRGBColors = Record<IAppearanceColor, [number, number, number]>;

/** The colours an operator can set for the booking portal; null means the default. */
export interface IAppearanceConfigColors {
  primary: string | null;
  secondary: string | null;
  background: string | null;
  text: string | null;
  disabled: string | null;
  darkDisabled: string | null;
  error: string | null;
  headerBackground: string | null;
  headerText: string | null;
}

export const defaultColors: IAppearanceHexColors = {
  primary: '#265E92',
  secondary: '#C4DDF1',
  danger: '#CA3E3E',
  success: '#69CA4A',
  text: '#132B40',
  white: '#fff',
  black: '#000',
  link: '#265E92',
  'gray-100': '#FAFAFA',
  'gray-200': '#EBEDEF',
  background: '#FAFAFA',
  'gray-500': '#D5D5D5',
  disabled: '#D5D5D5',
  'gray-700': '#8E8E8E',
  darkDisabled: '#8E8E8E',
  'header-background': '#fff',
  'header-text': '#000',
};

/** Maps the API colour names to the `--color-*` names, filling in the defaults. */
export function mapColors(
  colors: Partial<IAppearanceConfigColors>
): IAppearanceHexColors {
  return {
    primary: colors.primary || defaultColors.primary,
    secondary: colors.secondary || defaultColors.secondary,
    danger: colors.error || defaultColors.danger,
    success: defaultColors.success,
    text: colors.text || defaultColors.text,
    white: '#fff',
    black: '#000',
    link: colors.primary || defaultColors.link,
    'gray-100': defaultColors['gray-100'],
    background: colors.background || defaultColors['gray-100'],
    'gray-200': defaultColors['gray-200'],
    'gray-500': defaultColors['gray-500'],
    disabled: colors.disabled || defaultColors['gray-500'],
    'gray-700': defaultColors['gray-700'],
    darkDisabled: colors.darkDisabled || defaultColors['gray-700'],
    'header-background': colors.headerBackground || '#fff',
    'header-text': colors.headerText || '#000',
  };
}

export function convertMapToRGB(
  colorMap: IAppearanceHexColors | undefined
): IAppearanceRGBColors {
  return appearanceColors.reduce((all, colorName) => {
    return {
      ...all,
      [colorName]: getRGB(colorMap?.[colorName] || defaultColors[colorName]),
    };
  }, {} as Partial<IAppearanceRGBColors>) as IAppearanceRGBColors;
}

/**
 * The Payment Element look of the booking portal and the payment page, read from the `--color-*`
 * variables and `--font-family` the app wrote on `element`. `primaryColor` is what Stripe draws as
 * text on white.
 */
export function getStripeAppearance(
  element: Element,
  primaryColor: IAppearanceColor = 'primary'
): Appearance {
  const style = getComputedStyle(element);
  const getColor = (color: IAppearanceColor) =>
    `rgb(${style.getPropertyValue(`--color-${color}`).trim()})`;
  const border = `1px solid ${getColor('gray-500')}`;

  return {
    variables: {
      colorPrimary: getColor(primaryColor),
      colorBackground: getColor('white'),
      colorText: getColor('black'),
      colorTextPlaceholder: getColor('gray-500'),
      colorDanger: getColor('danger'),
      fontFamily: `${style.getPropertyValue('--font-family').trim()}, Ideal Sans, system-ui, sans-serif`,
      spacingUnit: '4px',
      borderRadius: '4px',
    },
    rules: {
      '.Tab': { border, boxShadow: 'none', marginBottom: '16px' },
      '.Input': { border, boxShadow: 'none', padding: '12px 16px' },
    },
  };
}
